import { useEffect, useRef, useState } from 'react';
import { MapPin, AlertCircle, Navigation } from 'lucide-react';
import { devError } from '../lib/devLog';
import { hubAPI } from '../lib/api';
import { setSafeHTML } from '../lib/safeHtml';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const MAPBOX_VERSION = '3.0.1';

// Load Mapbox from CDN (avoids worker/webpack issues in production)
const loadMapboxCDN = () => {
  return new Promise((resolve, reject) => {
    if (window.mapboxgl) { resolve(window.mapboxgl); return; }
    const link = document.createElement('link');
    link.href = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.css`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.js`;
    script.async = true;
    script.onload = () => resolve(window.mapboxgl);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

// Zone styling palette (by index, smallest → largest radius)
const ZONE_STYLES = [
  { color: '#F97316', borderColor: '#EA580C', opacity: 0.35 }, // Hub
  { color: '#2563EB', borderColor: '#1D4ED8', opacity: 0.22 },
  { color: '#059669', borderColor: '#047857', opacity: 0.18 },
  { color: '#7C3AED', borderColor: '#6D28D9', opacity: 0.16 },
  { color: '#DB2777', borderColor: '#BE185D', opacity: 0.14 },
  { color: '#DC2626', borderColor: '#B91C1C', opacity: 0.12 },
];

const milesToKm = (miles) => miles * 1.60934;

const generateCircle = (centerLng, centerLat, radiusKm, steps = 128) => {
  const coords = [];
  for (let i = 0; i <= steps; i++) {
    const angle = (2 * Math.PI * i) / steps;
    const dx = radiusKm / 111.0;
    const dy = radiusKm / (111.0 * Math.cos((centerLat * Math.PI) / 180));
    const lat = centerLat + dy * Math.sin(angle);
    const lng = centerLng + dx * Math.cos(angle);
    coords.push([lng, lat]);
  }
  return coords;
};

const ServiceAreaMap = ({ className = '', height = '400px', focusedHubId = null, onMapReady = null }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapError, setMapError] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [hubs, setHubs] = useState([]);
  const [primaryHub, setPrimaryHub] = useState(null);

  // Fetch hubs from API
  useEffect(() => {
    let cancelled = false;
    hubAPI.list()
      .then(res => {
        if (cancelled) return;
        const list = (res.data || []).map(h => ({
          ...h,
          lng: h.lon,
        }));
        if (list.length === 0) {
          // Fallback
          setHubs([{ name: 'Mascoutah', state: 'IL', lat: 38.4931, lng: -89.7932, is_primary: true, zones: [] }]);
          setPrimaryHub({ name: 'Mascoutah', state: 'IL', lat: 38.4931, lng: -89.7932, is_primary: true, zones: [] });
          return;
        }
        setHubs(list);
        setPrimaryHub(list.find(h => h.is_primary) || list[0]);
      })
      .catch(err => {
        devError('Failed to fetch hubs:', err);
        // Fallback to Mascoutah
        const fallback = { name: 'Mascoutah', state: 'IL', lat: 38.4931, lng: -89.7932, is_primary: true, zones: [] };
        setHubs([fallback]);
        setPrimaryHub(fallback);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!mapContainer.current || map.current || !primaryHub) return;

    const token = MAPBOX_TOKEN;
    if (!token || token === 'YOUR_MAPBOX_TOKEN_HERE') {
      setMapError('Mapbox token not configured. Please add your API key.');
      return;
    }

    const initMap = async () => {
      try {
        const mapboxgl = await loadMapboxCDN();
        mapboxgl.accessToken = token;

        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/light-v11',
          center: [primaryHub.lng, primaryHub.lat],
          zoom: 8.5,
          interactive: true,
          attributionControl: false,
          pitchWithRotate: false,
          dragRotate: false,
        });

        map.current.addControl(
          new mapboxgl.NavigationControl({ showCompass: false }),
          'top-left'
        );

        map.current.on('load', () => {
          if (!map.current) return;

          // Build features for all hubs — we'll show/hide via filter based on focusedHubId
          const features = [];
          hubs.forEach((hub, hubIdx) => {
            const sortedZones = [...(hub.zones || [])].sort((a, b) => (b.radius_miles || 0) - (a.radius_miles || 0));
            sortedZones.forEach((z, idxFromLargest) => {
              const styleIdx = sortedZones.length - 1 - idxFromLargest; // 0 = smallest = hub
              const style = ZONE_STYLES[styleIdx % ZONE_STYLES.length];
              features.push({
                type: 'Feature',
                properties: {
                  name: z.name,
                  hub: hub.name,
                  hub_id: hub.id || hub.name,
                  is_primary: !!hub.is_primary,
                  color: style.color,
                  borderColor: style.borderColor,
                  opacity: style.opacity,
                  zoneIdx: styleIdx,
                  uid: `h${hubIdx}-z${idxFromLargest}`,
                },
                geometry: {
                  type: 'Polygon',
                  coordinates: [generateCircle(hub.lng, hub.lat, milesToKm(z.radius_miles || 0))],
                },
              });
            });
          });

          if (!map.current.getSource('service-zones')) {
            map.current.addSource('service-zones', {
              type: 'geojson',
              data: { type: 'FeatureCollection', features },
            });
          }

          // Default filter: show only primary hub's zones (less clutter)
          const initialFilter = ['get', 'is_primary'];

          if (!map.current.getLayer('zone-fill')) {
            map.current.addLayer({
              id: 'zone-fill',
              type: 'fill',
              source: 'service-zones',
              filter: initialFilter,
              paint: {
                'fill-color': ['get', 'color'],
                'fill-opacity': ['get', 'opacity'],
              },
            });
          }

          if (!map.current.getLayer('zone-border')) {
            map.current.addLayer({
              id: 'zone-border',
              type: 'line',
              source: 'service-zones',
              filter: initialFilter,
              paint: {
                'line-color': ['get', 'borderColor'],
                'line-width': 1.5,
                'line-opacity': 0.7,
              },
            });
          }

          // CSS animation
          if (!document.getElementById('localmove-pulse-anim')) {
            const style = document.createElement('style');
            style.id = 'localmove-pulse-anim';
            style.textContent = `
              @keyframes lmPulse {
                0%, 100% { transform: scale(1); opacity: 1; }
                50% { transform: scale(1.3); opacity: 0.5; }
              }
            `;
            document.head.appendChild(style);
          }

          // Hub markers — primary larger with pulse, secondaries small and static
          hubs.forEach(hub => {
            const isPrimary = !!hub.is_primary;
            const hubMarkerEl = document.createElement('div');
            hubMarkerEl.className = 'hub-marker';
            hubMarkerEl.dataset.hubId = hub.id || hub.name;
            const size = isPrimary ? 44 : 22;
            const innerSize = isPrimary ? 28 : 18;
            const bg = isPrimary
              ? 'linear-gradient(135deg, #F97316, #EA580C)'
              : 'linear-gradient(135deg, #8B5CF6, #6D28D9)';
            const ringBg = 'rgba(249, 115, 22, 0.3)';
            const ringHtml = isPrimary
              ? `<div style="position:absolute;inset:0;background:${ringBg};border-radius:50%;animation:lmPulse 2.5s ease-in-out infinite;"></div>`
              : '';
            setSafeHTML(hubMarkerEl, `
              <div style="position:relative;width:${size}px;height:${size}px;cursor:pointer;">
                ${ringHtml}
                <div style="position:absolute;inset:${(size - innerSize) / 2}px;background:${bg};border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.25);border:${isPrimary ? 3 : 2}px solid white;">
                  <svg width="${isPrimary ? 14 : 10}" height="${isPrimary ? 14 : 10}" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
              </div>
            `);

            const popupNode = document.createElement('div');
            popupNode.style.cssText = 'padding: 8px 12px; text-align: center;';
            const title = document.createElement('strong');
            title.style.cssText = 'font-size: 14px; color: #18181B;';
            title.textContent = `${isPrimary ? 'Primary' : ''} LocalMove Hub`;
            const subtitle = document.createElement('p');
            subtitle.style.cssText = 'margin: 4px 0 0; font-size: 12px; color: #71717A;';
            subtitle.textContent = `${hub.name}, ${hub.state || 'IL'}`;
            const ratebadge = document.createElement('p');
            ratebadge.style.cssText = 'margin: 4px 0 0; font-size: 11px; color: #F97316; font-weight: 600;';
            ratebadge.textContent = 'Service Hub';
            popupNode.appendChild(title);
            popupNode.appendChild(subtitle);
            popupNode.appendChild(ratebadge);

            new mapboxgl.Marker({ element: hubMarkerEl, anchor: 'center' })
              .setLngLat([hub.lng, hub.lat])
              .setPopup(new mapboxgl.Popup({ offset: 30, closeButton: false }).setDOMContent(popupNode))
              .addTo(map.current);
          });

          // Fit bounds to all hubs (if more than 1)
          if (hubs.length > 1) {
            const bounds = new mapboxgl.LngLatBounds();
            hubs.forEach(h => bounds.extend([h.lng, h.lat]));
            map.current.fitBounds(bounds, { padding: 80, maxZoom: 9 });
          }

          setMapLoaded(true);
          if (onMapReady) onMapReady(map.current);
        });

        map.current.on('error', (e) => {
          devError('Mapbox error:', e);
          if (e.error?.status === 401) {
            setMapError('Invalid Mapbox token. Please check your API key.');
          } else {
            setMapError('Failed to load map. Please try again later.');
          }
        });
      } catch (error) {
        devError('Map initialization error:', error);
        setMapError('Failed to initialize map.');
      }
    };

    initMap();

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryHub]);

  // Fly to focused hub when selected + toggle zone filter
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    
    // Toggle which hub's zones are visible
    const applyZoneFilter = () => {
      if (!map.current.getLayer('zone-fill')) return;
      const filter = focusedHubId
        ? ['==', ['get', 'hub_id'], focusedHubId]
        : ['get', 'is_primary']; // default: only primary hub's zones
      map.current.setFilter('zone-fill', filter);
      map.current.setFilter('zone-border', filter);
    };
    applyZoneFilter();
    
    if (focusedHubId) {
      const hub = hubs.find(h => h.id === focusedHubId);
      if (!hub) return;
      map.current.flyTo({
        center: [hub.lng, hub.lat],
        zoom: 10.5,
        essential: true,
        duration: 1500,
      });
    } else if (hubs.length > 1) {
      // Reset to fit all hubs
      try {
        const mapboxgl = window.mapboxgl;
        if (!mapboxgl) return;
        const bounds = new mapboxgl.LngLatBounds();
        hubs.forEach(h => bounds.extend([h.lng, h.lat]));
        map.current.fitBounds(bounds, { padding: 80, maxZoom: 9, duration: 1200 });
      } catch (e) { devError('reset bounds error:', e); }
    }
  }, [focusedHubId, hubs, mapLoaded]);

  // Legend zones — determined by focused hub or primary hub
  const activeHub = focusedHubId ? hubs.find(h => h.id === focusedHubId) : primaryHub;
  const legendZones = (activeHub?.zones || []).slice().sort((a, b) => (a.radius_miles || 0) - (b.radius_miles || 0));

  // Fallback UI when map can't load
  if (mapError) {
    return (
      <div
        className={`relative bg-gradient-to-br from-zinc-50 to-zinc-100 rounded-2xl flex flex-col items-center justify-center border border-zinc-200 ${className}`}
        style={{ height }}
        data-testid="service-area-map-fallback"
      >
        <AlertCircle className="w-12 h-12 text-zinc-400 mb-4" />
        <p className="text-zinc-500 text-center px-4 mb-6">{mapError}</p>
        <div className="relative w-72 h-72">
          {legendZones.slice().reverse().map((zone, index) => {
            const size = 100 - (index * 14);
            const style = ZONE_STYLES[(legendZones.length - 1 - index) % ZONE_STYLES.length];
            return (
              <div
                key={zone.name}
                className="absolute rounded-full transition-all duration-300"
                style={{
                  width: `${size}%`,
                  height: `${size}%`,
                  left: `${(100 - size) / 2}%`,
                  top: `${(100 - size) / 2}%`,
                  backgroundColor: style.color,
                  opacity: style.opacity + 0.1,
                  border: `2px solid ${style.borderColor}`,
                }}
              />
            );
          })}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg border-4 border-white">
              <MapPin className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
        <p className="text-sm text-zinc-500 mt-4 font-medium">{primaryHub?.name || 'Mascoutah'}, {primaryHub?.state || 'IL'} - Service Hub</p>
      </div>
    );
  }

  return (
    <div
      className={`relative rounded-2xl overflow-hidden border border-zinc-200 ${className}`}
      style={{ height }}
      data-testid="service-area-map"
    >
      <div ref={mapContainer} className="w-full h-full" />

      {!mapLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-50 to-zinc-100 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-zinc-500">Loading map...</span>
          </div>
        </div>
      )}

      {/* Compact zone legend (collapsible on mobile via summary) */}
      <details className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg shadow-md border border-zinc-200 max-w-[220px] group" open>
        <summary className="cursor-pointer list-none px-3 py-2 flex items-center justify-between gap-2 select-none">
          <div className="flex items-center gap-1.5">
            <Navigation className="w-3.5 h-3.5 text-orange-500" />
            <span className="text-xs font-semibold text-zinc-800">
              {activeHub?.name || 'Service'} zones
            </span>
          </div>
          <span className="text-zinc-400 text-xs group-open:rotate-180 transition-transform">▾</span>
        </summary>
        <div className="px-3 pb-2 pt-1 space-y-1 border-t border-zinc-100">
          {legendZones.map((zone, idx) => {
            const style = ZONE_STYLES[idx % ZONE_STYLES.length];
            return (
              <div key={zone.name} className="flex items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-1.5 min-w-0">
                  <div
                    className="w-2.5 h-2.5 rounded-full border flex-shrink-0"
                    style={{ backgroundColor: style.color, borderColor: style.borderColor }}
                  />
                  <span className="text-zinc-700 truncate">{zone.name}</span>
                  <span className="text-zinc-400">·</span>
                  <span className="text-zinc-500">{zone.radius_miles}mi</span>
                </div>
                <span className={`font-semibold flex-shrink-0 ${zone.addon === 0 ? 'text-orange-600' : 'text-zinc-700'}`}>
                  {zone.addon === 0 ? '$0' : `+$${zone.addon}`}
                </span>
              </div>
            );
          })}
        </div>
      </details>
    </div>
  );
};

export default ServiceAreaMap;
